import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, Badge, HStack, Divider, List, ListItem, ListIcon } from "@chakra-ui/react";
import { FaDollarSign, FaGift, FaPercentage, FaUniversity, FaCheckCircle, FaMapMarkerAlt } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";
const SOLAR_GOLD_HOVER = "#a07e10";

const SolarResourcesIncentives: React.FC = () => {
  const federalIncentives = [
    {
      title: "Small-scale Technology Certificates (STCs)",
      amount: "$3,000 - $5,500",
      description: "Upfront discount on system purchase based on location and system size",
      eligibility: ["Systems under 100kW", "Clean Energy Council approved products", "Accredited installer required"]
    },
    {
      title: "Investment Tax Deduction",
      amount: "Up to 30%",
      description: "Immediate tax deduction for businesses installing solar",
      eligibility: ["Business entities only", "Asset must be used for business", "Installed and ready to use"]
    }
  ];

  const stateIncentives = [
    {
      state: "NSW",
      icon: FaMapMarkerAlt,
      programs: [
        "Peak Demand Reduction Scheme rebates",
        "Empowering Homes low-interest loans ($14,000)",
        "Regional NSW solar battery rebate ($1,600-2,400)"
      ]
    },
    {
      state: "VIC",
      icon: FaMapMarkerAlt,
      programs: [
        "Solar Homes Program rebate ($1,400)",
        "Solar battery rebate ($3,500)",
        "Interest-free loan available ($1,400)"
      ]
    },
    {
      state: "QLD",
      icon: FaMapMarkerAlt,
      programs: [
        "Battery Booster Program ($3,000-4,000)",
        "Interest-free loans for batteries ($10,000)",
        "Queensland Climate Smart Energy Savers Program"
      ]
    },
    {
      state: "SA",
      icon: FaMapMarkerAlt,
      programs: [
        "Home Battery Scheme ($3,000)",
        "Retailer Energy Productivity Scheme incentives",
        "Virtual Power Plant programs"
      ]
    },
    {
      state: "WA",
      icon: FaMapMarkerAlt,
      programs: [
        "Distributed Energy Buyback Scheme",
        "Regional and remote solar grants",
        "Various utility rebate programs"
      ]
    },
    {
      state: "TAS",
      icon: FaMapMarkerAlt,
      programs: [
        "Tasmanian Energy Saver Loan Scheme",
        "Solar and battery finance packages",
        "Premium feed-in tariff (legacy systems)"
      ]
    }
  ];

  const feedInTariffs = [
    { state: "NSW", rate: "5-20c/kWh", type: "Varies by retailer" },
    { state: "VIC", rate: "4.9c/kWh", type: "Minimum (retailers offer more)" },
    { state: "QLD", rate: "6-10c/kWh", type: "Varies by retailer" },
    { state: "SA", rate: "5-16c/kWh", type: "Varies by retailer" },
    { state: "WA", rate: "2.5-10c/kWh", type: "Varies by retailer" },
    { state: "TAS", rate: "8.9c/kWh", type: "Aurora standard rate" }
  ];

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/solar/resources/incentives"
        fallbackTitle="Solar Incentives Guide"
        fallbackDescription="Government rebates and incentive programs for solar installations"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center">
            <Icon as={FaGift} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Solar Rebates & Incentives
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="3xl">
              Maximise your savings with government rebates, tax incentives, and feed-in tariff programs
            </Text>
            <Badge colorScheme="yellow" fontSize="lg" px={6} py={3} borderRadius="full">
              Save up to $10,000+ on Your Solar System
            </Badge>
          </VStack>
        </Container>
      </Box>

      {/* Federal Incentives */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Badge bg={SOLAR_GOLD} color="white" fontSize="md" px={4} py={2}>FEDERAL PROGRAMS</Badge>
          <Heading size="xl" textAlign="center">Nationwide Solar Incentives</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
            These incentives are available across all Australian states and territories
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {federalIncentives.map((incentive, index) => (
            <Box
              key={index}
              p={8}
              bg="white"
              borderRadius="xl"
              border="3px solid"
              borderColor={SOLAR_GOLD}
              shadow="lg"
            >
              <HStack spacing={4} mb={4}>
                <Icon as={FaDollarSign} boxSize={10} color={SOLAR_GOLD} />
                <VStack align="flex-start" spacing={0}>
                  <Heading size="md">{incentive.title}</Heading>
                  <Badge bg={NAVY} color="white" fontSize="lg" px={3} py={1}>
                    {incentive.amount}
                  </Badge>
                </VStack>
              </HStack>

              <Text color="gray.700" mb={4}>
                {incentive.description}
              </Text>

              <Divider my={4} />

              <VStack align="stretch" spacing={2}>
                <Text fontWeight="bold" fontSize="sm" color="gray.600">ELIGIBILITY:</Text>
                {incentive.eligibility.map((item, idx) => (
                  <HStack key={idx} align="flex-start">
                    <Icon as={FaCheckCircle} color={SOLAR_GOLD} mt={1} />
                    <Text fontSize="sm" color="gray.600">{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* State-by-State Programs */}
      <Box bg="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} mb={12}>
            <Badge bg={SOLAR_GOLD} color="white" fontSize="md" px={4} py={2}>STATE & TERRITORY PROGRAMS</Badge>
            <Heading size="xl" textAlign="center">Regional Solar Incentives</Heading>
            <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
              Additional rebates and programs available in your state
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {stateIncentives.map((state, index) => (
              <Box
                key={index}
                p={6}
                bg="gray.50"
                borderRadius="xl"
                border="2px solid"
                borderColor={SOLAR_GOLD}
                shadow="md"
              >
                <HStack spacing={3} mb={4}>
                  <Icon as={state.icon} boxSize={8} color={SOLAR_GOLD} />
                  <Heading size="md" color={NAVY}>{state.state}</Heading>
                </HStack>

                <List spacing={2}>
                  {state.programs.map((program, pIndex) => (
                    <ListItem key={pIndex} fontSize="sm" color="gray.700">
                      <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                      {program}
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Feed-in Tariffs */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Badge bg={SOLAR_GOLD} color="white" fontSize="md" px={4} py={2}>ONGOING EARNINGS</Badge>
          <Heading size="xl" textAlign="center">Feed-in Tariff Rates</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
            Get paid for excess solar energy you export to the grid
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {feedInTariffs.map((tariff, index) => (
            <Box
              key={index}
              p={6}
              bg="gray.50"
              borderRadius="xl"
              border="2px solid"
              borderColor={SOLAR_GOLD}
              textAlign="center"
            >
              <Text fontSize="2xl" fontWeight="bold" color={NAVY} mb={2}>
                {tariff.state}
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color={brandConfig.colors.primary} mb={2}>
                {tariff.rate}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {tariff.type}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Box mt={8} p={6} bg="gray.50" borderRadius="lg" border="1px solid" borderColor={SOLAR_GOLD}>
          <HStack spacing={3} align="flex-start">
            <Icon as={FaPercentage} boxSize={6} color={SOLAR_GOLD} mt={1} />
            <VStack align="flex-start" spacing={2}>
              <Text fontWeight="bold" color={NAVY}>Pro Tip:</Text>
              <Text fontSize="sm" color="gray.700">
                Feed-in tariff rates vary by electricity retailer. Shop around for the best rate - some retailers
                offer premium rates up to 20c/kWh for solar exports. We can help you find the best deal.
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Container>

      {/* Financing Options */}
      <Box bg={brandConfig.colors.primary} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <Icon as={FaUniversity} boxSize={12} />
            <Heading size="xl" textAlign="center">Financing Options</Heading>
            <Text fontSize="lg" textAlign="center" maxW="3xl" opacity={0.9}>
              Multiple pathways to make solar affordable
            </Text>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full" pt={4}>
              <Box textAlign="center">
                <Text fontSize="3xl" fontWeight="bold" mb={2}>0%</Text>
                <Text fontSize="lg" fontWeight="semibold" mb={2}>Interest</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Available through various state programs
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="3xl" fontWeight="bold" mb={2}>5-7 Years</Text>
                <Text fontSize="lg" fontWeight="semibold" mb={2}>Loan Terms</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Flexible repayment periods to suit your budget
                </Text>
              </Box>
              <Box textAlign="center">
                <Text fontSize="3xl" fontWeight="bold" mb={2}>$0</Text>
                <Text fontSize="lg" fontWeight="semibold" mb={2}>Upfront Cost</Text>
                <Text fontSize="sm" opacity={0.8}>
                  Pay from your energy savings over time
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={16}>
        <Box
          p={10}
          bg="gray.50"
          borderRadius="2xl"
          border="2px solid"
          borderColor={SOLAR_GOLD}
          textAlign="center"
        >
          <Heading size="xl" mb={4} color={NAVY}>Ready to Claim Your Rebates?</Heading>
          <Text fontSize="lg" color="gray.700" mb={6} maxW="2xl" mx="auto">
            Our team will calculate all available incentives for your location and help you maximize your savings.
            We handle all the paperwork and rebate applications for you.
          </Text>
          <Badge bg={SOLAR_GOLD} color="white" fontSize="lg" px={6} py={3} borderRadius="full">
            Free Rebate Calculator - Get Your Personalized Estimate
          </Badge>
        </Box>
      </Container>

      <SolarFooter />
    </Box>
  );
};

export default SolarResourcesIncentives;
